import {FormControl, FormGroup} from '@angular/forms';

import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {InArrayValidator, InvalidAmountKey} from '@spa/validators';

import {
    ServerResponse,
    ServerResponseData,
    PaymentMethodsEnum,
    HttpStatusCodesEnum,
    PaymentProcessesEnum,
    PaymentProvidersEnum,
    FundingRequestPathsEnum,
    PaymentsService,
    IndividualsService,
    AccountInfoInterface,
    FundingWireTransferInterface,
    AccountInfoModel,
    LinkedAccountModel,
    TradingAccountModel,
    IndividualExtendedModel,
    InternalTransferInputModel,
    FundingWireTransferInputModel,
    CashAccountDetailedReportModel,
    TradingAccountDetailedReportModel,
    WireTransferBankAccountFilterModel
} from '@spa/api';

import {AmountControl, AutocompleteControl, BaseControl, Helper} from '../../../../shared/classes';

import {CountryCodesEnum, DepositFormErrorStatesEnum} from '../../enums';

import { MaximumAmountValidatorKey } from 'appEntries/panel/validators';


import {
    DepositAmountSelector,
    DepositAccountToSelector,
    DepositComponentControls,
    DepositAccountFromSelector,
    DepositLinkedAccountFromSelector
} from '../../../../../assets/descriptors';

export class DepositForm {
    private readonly paymentsService: PaymentsService;
    private readonly tradingAccounts: TradingAccountModel[];
    private readonly linkedAccounts: LinkedAccountModel[];
    private readonly individualsService: IndividualsService;
    private readonly account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;

    private paymentMethodsEnum: typeof PaymentMethodsEnum;
    private paymentProvidersEnum: typeof PaymentProvidersEnum;

    public form: FormGroup;
    public controls: BaseControl[];

    constructor(
        account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
        linkedAccounts: LinkedAccountModel[],
        tradingAccounts: TradingAccountModel[],
        paymentsService: PaymentsService,
        individualsService: IndividualsService
    ) {
        this.account = account;
        this.linkedAccounts = linkedAccounts;
        this.tradingAccounts = tradingAccounts;
        this.paymentsService = paymentsService;
        this.paymentMethodsEnum = PaymentMethodsEnum;
        this.paymentProvidersEnum = PaymentProvidersEnum;
        this.individualsService = individualsService;
    }

    private initControl(control: BaseControl): BaseControl {
        switch (control.name) {
            case DepositAccountFromSelector:
                const accountIds = [];

                (control as AutocompleteControl).options = this.tradingAccounts
                    .filter((account: TradingAccountModel) => account.id !== this.account.accountId)
                    .map((account: TradingAccountModel) => {
                        accountIds.push(account.id);

                        return {
                            label: account.accountReferenceId,
                            value: account.id
                        };
                    });

                control.extendValidators(InArrayValidator(accountIds));
                break;
            case DepositLinkedAccountFromSelector:
                const linkedAccountIds = this.linkedAccounts
                    .map((linkedAccount: LinkedAccountModel) => linkedAccount.id);

                (control as AutocompleteControl).options =
                    this.linkedAccounts.map((linkedAccount: LinkedAccountModel) => {
                        return {
                            label: linkedAccount.accountInfo.bankName,
                            value: linkedAccount.id
                        };
                    });

                control.extendValidators(InArrayValidator(linkedAccountIds));
                break;
            case DepositAccountToSelector:
                control.value = this.account.accountReferenceId;
                break;
            case DepositAmountSelector:
                (control as AmountControl).currency = this.account.currencyCode;
                break;
        }

        return control;
    }

    private createBankDeposit(amount: string): ServerResponse<boolean> {
        if (!this.account.isOwnerCompany) {
            return this.individualsService.getExtendedById(this.account.ownerId, false).pipe(
                switchMap((getIndividualResponse: ServerResponseData<IndividualExtendedModel>) => {
                    if (getIndividualResponse.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<IndividualExtendedModel>(
                            getIndividualResponse,
                            DepositFormErrorStatesEnum.GetClientFailed
                        );
                    }

                    if (!getIndividualResponse.data) {
                        const noDataError = Helper.errorsHelper().createResponseForNoData(
                            DepositFormErrorStatesEnum.GetClientFailed
                        );

                        return of(noDataError);
                    }

                    const model = getIndividualResponse.data as IndividualExtendedModel;
                    const isInitialDeposit = (model.initialDepositId === 0);

                    return this.getDefaultVendorBankAccount(amount, isInitialDeposit);
                })
            );
        }

        return Helper.createSuccessResponseObservable<boolean>(false).pipe(
            switchMap(() => {
                return this.getDefaultVendorBankAccount(amount, false);
            })
        );
    }

    private getDefaultVendorBankAccount(amount: string, isInitialDeposit: boolean): ServerResponse<boolean> {
        const vendorBankAccountFilter = new WireTransferBankAccountFilterModel(
            this.account.currencyCode,
            CountryCodesEnum.Bulgaria,
            PaymentProcessesEnum.Funding,
            this.account.accountId
        );

        return this.paymentsService.getWireTransferBankAccount(vendorBankAccountFilter).pipe(
            switchMap((response: ServerResponseData<AccountInfoInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable(
                        response,
                        DepositFormErrorStatesEnum.GetDefaultVendorBankAccountFailed
                    );
                }

                if (!response.data) {
                    const apiError = Helper.errorsHelper().createResponseForNoData(
                        DepositFormErrorStatesEnum.DefaultVendorBankAccountNotFound
                    );

                    return of(apiError);
                }

                const model = new AccountInfoModel(response.data as AccountInfoInterface);

                return this.createBankDepositRequest(model, amount, isInitialDeposit);
            })
        );
    }

    private createBankDepositRequest(
        vendorBankAccount: AccountInfoModel,
        amount: string,
        isInitialDeposit: boolean
    ): ServerResponse<boolean> {
        const linkedAccountId = this.form.controls[DepositLinkedAccountFromSelector].value;

        const linkedAccounts = this.linkedAccounts.filter((account: LinkedAccountModel) => {
            return account.id === linkedAccountId;
        });

        const model = new FundingWireTransferInputModel(
            this.account.accountId,
            amount,
            (linkedAccounts.length !== 0) ? linkedAccounts[0].accountInfo : null,
            vendorBankAccount,
            isInitialDeposit,
            this.account.currencyCode
        );

        return this.paymentsService.registerFunding<FundingWireTransferInputModel, FundingWireTransferInterface>(
            model,
            FundingRequestPathsEnum.WireTransfer
        ).pipe(
            map((registerFundingResponse: ServerResponseData<FundingWireTransferInterface>) => {
                if (registerFundingResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<FundingWireTransferInterface>(
                        registerFundingResponse,
                        DepositFormErrorStatesEnum.RegisterBankDepositFailed
                    );
                }

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    initForm(method: number): void {
        this.form = new FormGroup({});

        this.controls = DepositComponentControls()
            .filter((control: BaseControl) => {
                if (
                    (method === PaymentMethodsEnum.InternalTransfer) &&
                    (control.name === DepositLinkedAccountFromSelector)
                ) {
                    return false;
                }

                return !((method === PaymentMethodsEnum.BankTransfer) &&
                    (control.name === DepositAccountFromSelector));
            })
            .map((control: BaseControl) => {
                control = this.initControl(control);

                this.form.addControl(control.name, new FormControl(control.value, control.validators));

                return control;
            });
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlMaxLength?: number) => { [key: string]: string; },
        method: number,
        accountFrom?: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
        unavailableCashMessageKey?: string,
        notEnoughMoneyMessageKey?: string,
        maxDepositAmount?: number
    ): boolean {
        const statuses = this.controls.map((control: BaseControl) => {
            const formControl = this.form.controls[control.name];

            if (!formControl) {
                return false;
            }

            if (formControl.invalid) {
                control.errors = Object.keys(formControl.errors).map((errorKey: string) => {
                    return messageFunction(control.placeholder)[errorKey];
                });

                return false;
            }

            const amountControlValue = this.form.controls[DepositAmountSelector].value;

            if (amountControlValue !== null) {
                const fixedAmount = parseFloat(Helper.compactString(amountControlValue));

                switch (control.name) {
                    case DepositAmountSelector:
                        if (fixedAmount === 0) {
                            control.setError(messageFunction()[InvalidAmountKey]);

                            return false;
                        }
                        if (maxDepositAmount && fixedAmount > maxDepositAmount) {
                            control.setError(messageFunction(control.placeholder, maxDepositAmount)[MaximumAmountValidatorKey]);

                            return false;
                        }
                        break;
                    case DepositAccountFromSelector:
                        if (method === PaymentMethodsEnum.InternalTransfer) {
                            let availableAccountFromCash = null;

                            if (accountFrom) {
                                availableAccountFromCash = accountFrom.availableCash.toFixed(2);
                            }

                            if (availableAccountFromCash === null) {
                                control.setError(messageFunction()[unavailableCashMessageKey]);

                                return false;
                            }

                            if (fixedAmount > parseFloat(availableAccountFromCash)) {
                                control.setError(messageFunction()[notEnoughMoneyMessageKey]);

                                return false;
                            }
                        }
                        break;
                }
            }

            return true;
        });

        return statuses.indexOf(false) < 0;
    }

    createRequest(method: number): ServerResponse<boolean> {
        const amount = Helper.compactString(this.form.controls[DepositAmountSelector].value);

        switch (method) {
            case PaymentMethodsEnum.BankTransfer:
                return this.createBankDeposit(amount);
            case PaymentMethodsEnum.InternalTransfer:
                const model = new InternalTransferInputModel(
                    parseFloat(amount),
                    this.form.controls[DepositAccountFromSelector].value,
                    this.account.accountId,
                    this.account.currencyCode
                );

                return this.paymentsService.registerInternalTransfer(model).pipe(
                    map((response: ServerResponseData<string>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<string>(
                                response,
                                DepositFormErrorStatesEnum.RegisterInternalDepositFailed
                            );
                        }

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        const error = Helper.errorsHelper()
            .createResponseForInvalidData(DepositFormErrorStatesEnum.CanFindCorrectPaymentMethod);

        return of(error);
    }
}
